import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLang } from '../../hooks/useLang';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import '../../styles/auth/ResetPassword.scss';

const ResetPassword = () => {
    const { urlLanguage } = useLang();
    const navigate = useNavigate();
    const location = useLocation();

    // Check if email was passed from ForgotPassword page
    const initialEmail = location.state?.email || '';

    const [email, setEmail] = useState(initialEmail);
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // If no email was passed, warn user
    useEffect(() => {
        if (!initialEmail) {
            setError("Email missing. Please enter your email manually or restart the process.");
        }
    }, [initialEmail]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            setIsLoading(false);
            return;
        }

        if (code.length !== 6) {
            setError('Please enter the 6-digit verification code.');
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, newPassword }),
            });

            const json = await response.json();

            if (!response.ok) {
                setError(json.error || 'Failed to reset password.');
            } else {
                setSuccessMessage(json.message);
                setTimeout(() => {
                    navigate(urlLanguage('/login'));
                }, 2000);
            }
        } catch (err) {
            console.error(err);
            setError('Network error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="reset-password-page">
            <div className="auth-logo">
                <FontAwesomeIcon icon={faBookOpen} className="auth-logo-icon" />
                YeniDers
            </div>

            <div className="auth-header">
                <h1>Reset Password</h1>
                <p>Enter the 6-digit code sent to your email</p>
            </div>

            <div className="auth-card">
                <form onSubmit={handleSubmit}>
                    {error && <div className="auth-error">{error}</div>}
                    {successMessage && <div className="auth-success">{successMessage} Redirecting to login...</div>}

                    <div className="auth-form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className="auth-form-group">
                        <label>Verification Code</label>
                        <input
                            type="text"
                            placeholder="6-digit code"
                            value={code}
                            maxLength={6}
                            onChange={(e) => setCode(e.target.value)}
                            required
                        />
                    </div>

                    <div className="auth-form-group password-group">
                        <label>New Password</label>
                        <div className="input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                            <FontAwesomeIcon
                                className="password-toggle-icon"
                                icon={showPassword ? faEyeSlash : faEye}
                                onClick={() => setShowPassword(!showPassword)}
                            />
                        </div>
                    </div>

                    <div className="auth-form-group password-group">
                        <label>Confirm New Password</label>
                        <div className="input-wrapper">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                            <FontAwesomeIcon
                                className="password-toggle-icon"
                                icon={showConfirmPassword ? faEyeSlash : faEye}
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            />
                        </div>
                    </div>

                    <button type="submit" className="auth-submit-btn" disabled={isLoading || successMessage}>
                        {isLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>

                <div className="auth-footer">
                    Back to <Link to={urlLanguage('/login')}>Login</Link>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
